/**
 * What's New history — accessible from the user menu.
 * Lists every visible whatsnew entry (newest first) with title, items, and "why".
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhatsNewHistoryPage({ user }) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/whatsnew/history`, {
          headers: { 'X-User-Id': user.id },
        });
        if (!cancelled) setEntries(r.data?.entries || []);
      } catch (e) {
        /* non-blocking */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="whatsnew-history-page">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4 -ml-2"
          data-testid="whatsnew-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 leading-tight">What's new in DrawReady</h1>
            <p className="text-sm text-slate-500">Every shipped update — most recent first.</p>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : entries.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500">
            No updates yet.
          </div>
        ) : (
          <div className="space-y-5">
            {entries.map((e) => (
              <article
                key={e.version}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
                data-testid={`whatsnew-history-entry-${e.version}`}
              >
                <header className="px-6 py-4 border-b border-slate-100 bg-slate-50/60">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-base font-bold text-slate-900">{e.title}</h2>
                      {e.subtitle && (
                        <p className="text-sm text-slate-600 mt-0.5">{e.subtitle}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{e.version}</div>
                      <div className="text-xs text-slate-500">{e.released ? formatDate(e.released) : ''}</div>
                    </div>
                  </div>
                </header>

                <div className="px-6 py-5 space-y-4">
                  {(e.items || []).map((it) => (
                    <div key={it.n} className="flex gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center">
                        {it.n}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{it.title}</div>
                        <p className="text-sm text-slate-600 mt-0.5">{it.body}</p>
                      </div>
                    </div>
                  ))}

                  {e.why && (
                    <div className="mt-3 bg-slate-50 border-l-4 border-blue-500 px-4 py-3 rounded-r-lg">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Why we built it</div>
                      <p className="text-sm text-slate-700">{e.why}</p>
                    </div>
                  )}

                  {e.cta_url && (
                    <div className="pt-2">
                      <Button
                        size="sm"
                        onClick={() => navigate(e.cta_url)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`whatsnew-history-cta-${e.version}`}
                      >
                        {e.cta_text || 'Open'} →
                      </Button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
